import React, { useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import SectionLabel from '../components/SectionLabel';
import AttendeeRow from '../components/AttendeeRow';
import Skeleton, { EmptyState } from '../components/Skeleton';
import { fetchAttendees, fetchSupplierAttendees, fetchGuests, useLive } from '../data/live';

// Module-scope so useMemo deps stay stable (no exhaustive-deps churn).
// Match each search term independently against the combined fields, so a
// "First Last" query still matches when the data has stray/double spaces
// (common in imported names) or the words are in a different order.
function matches(a, q) {
  if (!q) return true;
  const haystack = `${a.first} ${a.last} ${a.role} ${a.org}`.toLowerCase();
  return q.split(/\s+/).filter(Boolean).every((term) => haystack.includes(term));
}

export default function AttendeesPage() {
  const { meeting } = useOutletContext();
  const event = meeting._event;

  // Provider members (executives / CEOs).
  const providers = useLive(fetchAttendees, [event?.id]);
  // Corporate members & sponsors — sections hide until at least one
  // registration is flagged show_on_portal in Mission Control.
  const suppliers = useLive(fetchSupplierAttendees, [event?.id]);
  // Guests (speakers / staff / VIPs) — name, title, company, email.
  const guests = useLive(fetchGuests, [event?.id]);
  const loading = providers === undefined || suppliers === undefined || guests === undefined;

  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('all'); // all | providers | corporate | sponsors
  const q = query.trim().toLowerCase();

  // Derive filtered lists from the stable useLive state (providers/suppliers) so
  // the useMemo dependency arrays don't reference arrays re-created each render.
  const filteredProviders = useMemo(
    () => (providers || []).filter((a) => matches(a, q)),
    [q, providers],
  );
  const filteredCorporate = useMemo(
    () => (suppliers?.corporateMembers || []).filter((a) => matches(a, q)),
    [q, suppliers],
  );
  const filteredSponsors = useMemo(
    () => (suppliers?.sponsors || []).filter((a) => matches(a, q)),
    [q, suppliers],
  );
  const filteredGuests = useMemo(
    () => (guests || []).filter((a) => matches(a, q)),
    [q, guests],
  );

  const providerTotal = providers?.length || 0;
  const corporateTotal = suppliers?.corporateMembers?.length || 0;
  const sponsorTotal = suppliers?.sponsors?.length || 0;
  const guestTotal = guests?.length || 0;

  // Only surface the group filter when there's more than one group to jump to.
  const hasSuppliers = corporateTotal > 0 || sponsorTotal > 0 || guestTotal > 0;
  const chips = [
    { key: 'all', label: 'All' },
    { key: 'providers', label: 'Providers' },
    ...(corporateTotal > 0 ? [{ key: 'corporate', label: 'Corporate Members' }] : []),
    ...(sponsorTotal > 0 ? [{ key: 'sponsors', label: 'Sponsors' }] : []),
    ...(guestTotal > 0 ? [{ key: 'guests', label: 'Guests' }] : []),
  ];

  const showProviders = (group === 'all' || group === 'providers') && providerTotal > 0;
  const showCorporate = (group === 'all' || group === 'corporate') && corporateTotal > 0;
  const showSponsors = (group === 'all' || group === 'sponsors') && sponsorTotal > 0;
  const showGuests = (group === 'all' || group === 'guests') && guestTotal > 0;

  if (loading) return <Skeleton variant="row" count={6} />;
  if (!providerTotal && !hasSuppliers) {
    return <EmptyState>The attendee list will appear here as registrations are confirmed.</EmptyState>;
  }

  return (
    <>
      <input
        className="portal-search"
        type="search"
        placeholder="Search attendees…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {hasSuppliers && (
        <div className="portal-attendee-filter">
          {chips.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`portal-attendee-chip${group === c.key ? ' active' : ''}`}
              onClick={() => setGroup(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      {showProviders && (
        <>
          <SectionLabel right={`${filteredProviders.length} / ${providerTotal}`}>
            Provider Members
          </SectionLabel>
          <div className="portal-attendee-grid">
            {filteredProviders.map((a, i) => <AttendeeRow key={i} attendee={a} />)}
          </div>
        </>
      )}

      {showCorporate && (
        <>
          <SectionLabel right={`${filteredCorporate.length} / ${corporateTotal}`}>
            Corporate Members
          </SectionLabel>
          <div className="portal-attendee-grid">
            {filteredCorporate.map((a, i) => <AttendeeRow key={`cm-${i}`} attendee={a} />)}
          </div>
        </>
      )}

      {showSponsors && (
        <>
          <SectionLabel right={`${filteredSponsors.length} / ${sponsorTotal}`}>
            Sponsors
          </SectionLabel>
          <div className="portal-attendee-grid">
            {filteredSponsors.map((a, i) => <AttendeeRow key={`sp-${i}`} attendee={a} />)}
          </div>
        </>
      )}

      {showGuests && (
        <>
          <SectionLabel right={`${filteredGuests.length} / ${guestTotal}`}>
            Guests
          </SectionLabel>
          <div className="portal-attendee-grid">
            {filteredGuests.map((a, i) => <AttendeeRow key={`g-${i}`} attendee={a} />)}
          </div>
        </>
      )}
    </>
  );
}
